import React, { useState } from "react";
import { BRAND_COLOR } from "../constants";
import { useIntl } from "react-intl";
import { log } from "../common/common.logger";

interface RefreshButtonProps {
	checkAppVersion: () => Promise<boolean>;
}

/**
 * Drawer refresh button that check app version.
 * Based on the result, it reloads the app or shows "up to date" message (with progress animation).
 * @returns
 */
const RefreshButton: React.FC<RefreshButtonProps> = ({ checkAppVersion }) => {
	const intl = useIntl();

	const [loading, setLoading] = useState(false);
	const [status, setStatus] = useState<"default" | "upToDate">("default");

	const handleClick = async () => {
		if (loading) return;
		setLoading(true);

		const isNewer = await checkAppVersion();

		if (!isNewer) {
			setStatus("upToDate");
			// Reset back after animation (2.5s)
			setTimeout(() => setStatus("default"), 2500);
		} else {
			log("New version available!");
		}

		setLoading(false);
	};

	return (
		<button
			className={`btn w-100 mb-1 d-flex justify-content-between align-items-center position-relative overflow-hidden ${
				status === "upToDate" ? "btn-success text-white" : "btn-light text-dark"
			}`}
			onClick={handleClick}
			disabled={loading}
			style={{
				transition: "background 0.3s ease, color 0.3s ease",
			}}
		>
			{/* Animated red overlay */}
			{status === "upToDate" && (
				<span
					className={`position-absolute top-0 start-0 h-100`}
					style={{
						width: "100%",
						animation: "wipeLeft 2.5s forwards",
						zIndex: 0,
						backgroundColor: BRAND_COLOR,
					}}
				></span>
			)}

			{/* Content */}
			<span className="d-flex justify-content-between align-items-center w-100" style={{ zIndex: 1 }}>
				<span>
					{status === "upToDate"
						? intl.formatMessage({ id: "up_to_date" })
						: intl.formatMessage({ id: "refresh" })}
				</span>
				<i className={status === "upToDate" ? "bi bi-check-circle" : "bi bi-arrow-repeat"}></i>
			</span>

			{/* Wipe animation */}
			<style>
				{`
					@keyframes wipeLeft {
						0% { transform: translateX(0); }
						100% { transform: translateX(100%); }
					}
				`}
			</style>
		</button>
	);
};

export default RefreshButton;
